import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";
import { roleCategories } from "../constants/specialties";

// Map categories to CSS classes to match the image colors
const getCategoryClass = (category) => {
    if (!category) return "";
    
    // Direct mapping based on the categories shown in the image
    const categoryMap = {
        "Business/Consulting": "business-consulting",
        "Clinical Medicine": "clinical-medicine",
        "Clinical Research": "clinical-research", 
        "Entrepreneurship": "entrepreneurship",
        "HealthTech": "health-tech",
        "Hospital/Clinic Management": "hospital-clinic-management",
        "Insurance": "insurance",
        "Marketing/Sales": "marketing-sales",
        "Medical Communication": "medical-communication",
        "Medical Education": "medical-education",
        "Pharmaceuticals": "pharmaceuticals",
        "Policy/Legal/Ethics": "policy-legal-ethics",
        "Project Management": "project-management",
        "Wellness/lifestyle": "wellness-lifestyle"
    };
    
    return categoryMap[category] || "business-consulting";
};

// Use the categories that match the image design
const CATEGORY_OPTIONS = roleCategories;

const FilterChip = ({ label, active, onClick }) => {
    const categoryClass = getCategoryClass(label);
    return (
        <button
            className={`category-pill ${categoryClass} ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            {label}
        </button>
    );
};

const CompanyCard = ({ company }) => {
    const navigate = useNavigate();
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [currentImageUrl, setCurrentImageUrl] = useState(company.logo_url);
    
    // Update currentImageUrl when company.logo_url changes
    useEffect(() => {
        if (company.logo_url) {
            setCurrentImageUrl(company.logo_url);
            setImageError(false);
            setImageLoading(true);
        } else {
            setCurrentImageUrl(null);
            setImageError(false);
            setImageLoading(false);
        }
    }, [company.logo_url]);
    
    const handleImageError = () => {
        setImageError(true);
        setImageLoading(false);
    };
    
    const handleImageLoad = () => {
        setImageLoading(false);
        setImageError(false);
    };
    
    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 flex items-center justify-between employee-company-card">
            <div className="flex items-center gap-4 flex-1 min-w-0 pr-4">
                {currentImageUrl && !imageError ? (
                    <div className="relative w-10 h-10 flex-shrink-0">
                        {imageLoading && (
                            <div className="absolute inset-0 w-10 h-10 bg-gray-100 rounded-full animate-pulse" />
                        )}
                        <img 
                            src={currentImageUrl} 
                            alt={`${company.name} logo`}
                            className={`w-10 h-10 rounded-full object-cover border border-gray-200 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                        />
                    </div>
                ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#061B16]">{company.name}</div>
                    {company.locationSummary && (
                        <div className="text-xs text-gray-500 mt-1 break-words">{company.locationSummary}</div>
                    )}
                    {company.primaryCategory && (
                        <div
                            className={`mt-2 inline-flex text-xs rounded-full px-3 py-1 border category-pill ${getCategoryClass(
                                company.primaryCategory
                            )}`}
                        >
                            {company.primaryCategory}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-6 flex-shrink-0">
                <div className="text-xs text-gray-500 whitespace-nowrap">{company.openJobs} Open {company.openJobs === 1 ? 'job' : 'jobs'}</div>
                <button 
                    onClick={() => navigate(`/employee/company/${company.employerId}`)}
                    className="bg-black text-white rounded-full px-5 py-2 text-sm hover:bg-gray-800 whitespace-nowrap"
                >
                    View Company
                </button>
            </div>
        </div>
    );
};

const EmployeeCompanySearch = () => {
    const { user } = useAuth();
    // Jobs and filters (mirrors job board behaviour)
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [categoryFilter, setCategoryFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");
    const [provinceFilter, setProvinceFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const [availableCategories, setAvailableCategories] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [availableProvinces, setAvailableProvinces] = useState([]);

    // Fetch internal job listings from Supabase
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                setLoading(true);

                let jobListings = [];
                if (user?.email.includes('@fourminutemedicine.com')) {
                    try {
                        jobListings = await fetchAllIncludingDevModeJobs();
                    } catch (error) {
                        console.error("Error fetching dev mode jobs:", error);
                        throw error;
                    }
                } else {
                    try {
                        jobListings = await fetchAllExcludingDevModeJobs();
                    } catch (error) {
                        console.error("Error fetching non-dev mode jobs:", error);
                        throw error;
                    }
                }

                const sorted = jobListings ? [...jobListings] : [];
            
            // Build logo URL map from logo_filename
            const employerLogos = {};
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            
            // Get unique employer IDs that need logo processing
            const employersToProcess = new Map();
            sorted.forEach(job => {
                if (job.logo_filename && job.employer_id && !employersToProcess.has(job.employer_id)) {
                    employersToProcess.set(job.employer_id, job.logo_filename);
                }
            });
            
            // Process logos in parallel for better performance
            const logoPromises = Array.from(employersToProcess.entries()).map(async ([employerId, originalFilename]) => {
                try {
                    const { data: files, error: listError } = await supabase.storage
                        .from(bucket)
                        .list(employerId, {
                            limit: 100,
                            offset: 0,
                            sortBy: { column: 'created_at', order: 'desc' }
                        });
                    
                    if (!listError && files && files.length > 0) {
                        const logoFiles = files.filter(f => f.name.startsWith('company_logo_'));
                        
                        if (logoFiles.length > 0) {
                            let logoFile = null;
                            
                            if (originalFilename) {
                                logoFile = logoFiles.find(f => 
                                    f.name.endsWith(`_${originalFilename}`) ||
                                    f.name.endsWith(originalFilename) ||
                                    f.name.includes(originalFilename)
                                );
                            }
                            
                            if (!logoFile) {
                                logoFile = logoFiles.sort((a, b) => {
                                    const aTime = a.created_at || a.updated_at || '';
                                    const bTime = b.created_at || b.updated_at || '';
                                    return bTime.localeCompare(aTime);
                                })[0];
                            }
                            
                            if (logoFile) {
                                const logoPath = `${employerId}/${logoFile.name}`;
                                const { data: logoUrlData } = supabase.storage.from(bucket).getPublicUrl(logoPath);
                                if (logoUrlData?.publicUrl) {
                                    return { employerId, logoUrl: logoUrlData.publicUrl };
                                }
                            }
                        }
                    }
                } catch (err) {
                    // Silently fail and continue
                }
                return null;
            });
            
            const logoResults = await Promise.all(logoPromises);
            logoResults.forEach(result => {
                if (result) {
                    employerLogos[result.employerId] = result.logoUrl;
                }
            });
            
            // Attach logo URLs to jobs
            const jobsWithLogos = sorted.map(job => ({
                ...job,
                logo_url: employerLogos[job.employer_id] || job.logo_url || null
            }));
            
            setJobs(jobsWithLogos);

                // Build filter options from internal job listings
                const categoriesFromJobs = [...new Set(sorted.map(j => j.industry))].filter(Boolean).sort();
                const locations = [...new Set(sorted.map(j => j.location))].filter(Boolean).sort();
                const industries = [...new Set(sorted.map(j => j.industry))].filter(Boolean).sort();

                // Only show category pills (Business/Consulting, Clinical Medicine, Clinical Research, etc.)
                // when at least one job listing actually uses that category (via `specialty`).
                const categoriesWithJobs = CATEGORY_OPTIONS.filter((cat) =>
                    categoriesFromJobs.includes(cat)
                );

                setAvailableCategories(categoriesWithJobs);
                setAvailableLocations(locations);
                setAvailableProvinces([]); // Not used for internal jobs
            } catch (e) {
                setError('Failed to load job listings');
            } finally {
                setLoading(false);
            }
        };
        fetchJobs();
    }, []);

    const fetchAllIncludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .order('created_at', { ascending: false });
        return jobs;
    };

    const fetchAllExcludingDevModeJobs = async () => {
        const { data: jobs, error: jobsError } = await supabase
            .from('internal_job_listings')
            .select('*')
            .eq('is_dev_job', false)
            .order('created_at', { ascending: false });
        return jobs;
    };

    // Filter jobs using same logic as job board
    const filteredJobs = useMemo(() => {
        let result = jobs;

        if (categoryFilter) result = result.filter(j => j.industry === categoryFilter);
        if (locationFilter) result = result.filter(j => j.location === locationFilter);
        if (provinceFilter) result = result.filter(j => j.location && j.location.toLowerCase().includes(provinceFilter.toLowerCase()));
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(j => (j.company_name && j.company_name.toLowerCase().includes(q)) || (j.title && j.title.toLowerCase().includes(q)));
        }
        return result;
    }, [jobs, categoryFilter, locationFilter, provinceFilter, searchQuery]);

    // Group filtered jobs by company
    const companies = useMemo(() => {
        const map = new Map();
        filteredJobs.forEach(job => {
            const name = job.company_name || 'Unknown Company';
            const employerId = job.employer_id;
            
            if (!map.has(employerId)) {
                map.set(employerId, {
                    name,
                    employerId,
                    openJobs: 0,
                    locations: new Set(),
                    categories: new Set(),
                    logo_url: job.logo_url || null, // Get logo URL from job listing
                });
            }
            const entry = map.get(employerId);
            entry.openJobs += 1;
            if (job.location) entry.locations.add(job.location);
            // Track all distinct specialties for this company so we can show multiple
            // category pills if they hire across several sectors.
            if (job.industry) entry.categories.add(job.industry);
            // Keep the first logo_url found (all jobs from same employer should have same logo)
            if (!entry.logo_url && job.logo_url) {
                entry.logo_url = job.logo_url;
            }
        });

        return Array.from(map.values()).map(c => {
            const categories = Array.from(c.categories);
            return {
                ...c,
                locationSummary: c.locations.size ? Array.from(c.locations).slice(0,2).join(', ') : '',
                // Primary category used for display on the company card
                primaryCategory: categories[0] || '',
                // Keep full list in case it's useful elsewhere (not rendered on the card)
                categories,
            };
        });
    }, [filteredJobs]);

    const handleCategoryClick = (category) => {
        setCategoryFilter(prev => prev === category ? '' : category);
    };

    const handleClearAll = () => {
        setCategoryFilter('');
        setLocationFilter('');
        setProvinceFilter('');
        setSearchQuery('');
    };

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employee-company-search-container {
                    padding: 1rem !important;
                }
                .employee-company-search-container h1 {
                    font-size: 1.5rem !important;
                    margin-bottom: 1rem !important;
                }
                .employee-company-search-input {
                    padding: 0.75rem 0.75rem 0.75rem 2.5rem !important;
                    font-size: 0.875rem !important;
                }
                .employee-company-search-filters {
                    display: none !important;
                }
                .employee-company-search-filters-mobile {
                    display: block !important;
                    margin-bottom: 1rem !important;
                }
                .employee-company-search-filters-mobile select {
                    width: 100% !important;
                    padding: 0.75rem !important;
                    font-size: 0.875rem !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 0.5rem !important;
                }
                .employee-company-search-selected-category {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    gap: 0.5rem !important;
                    margin-top: 0.75rem !important;
                }
                .employee-company-search-selected-category span {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.75rem !important;
                }
                .employee-company-search-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
            }
            @media (min-width: 768px) and (max-width: 1024px) {
                .employee-company-search-container {
                    padding-left: 1rem !important;
                    padding-right: 1rem !important;
                }
                .employee-company-card {
                    padding: 1rem !important;
                    flex-direction: row !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    gap: 0.75rem !important;
                }
                .employee-company-card > div:first-child {
                    flex: 1 !important;
                    min-width: 0 !important;
                    padding-right: 0.75rem !important;
                    overflow: hidden !important;
                }
                .employee-company-card > div:last-child {
                    flex-shrink: 0 !important;
                    flex-direction: row !important;
                    gap: 0.5rem !important;
                    align-items: center !important;
                }
                .employee-company-card > div:last-child > div:first-child {
                    font-size: 0.7rem !important;
                    white-space: nowrap !important;
                }
                .employee-company-card button {
                    width: auto !important;
                    padding: 0.5rem 0.875rem !important;
                    font-size: 0.8rem !important;
                    white-space: nowrap !important;
                }
            }
            @media (max-width: 767px) {
                .employee-company-card {
                    padding: 1rem !important;
                    flex-direction: column !important;
                    gap: 1rem !important;
                }
                .employee-company-card > div:first-child {
                    width: 100% !important;
                }
                .employee-company-card > div:last-child {
                    width: 100% !important;
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                    align-items: flex-start !important;
                }
                .employee-company-card button {
                    width: 100% !important;
                    padding: 0.625rem 1rem !important;
                    font-size: 0.875rem !important;
                }
            }
        `}</style>
        <div className="max-w-7xl mx-auto employee-company-search-container">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-[#061B16]">Company Search</h1>
            </div>

            <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3 employee-company-search-bar">
                <div className="relative w-full md:w-[70%]">
                    <input
                        placeholder="Search Company"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-10 pr-4 h-11 outline-none employee-company-search-input"
                    />
                    <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="11" cy="11" r="7" strokeWidth="2" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" strokeWidth="2" />
                    </svg>
                </div>
                <div className="w-full md:w-[30%] flex md:justify-start md:pl-3">
                    <select 
                        value={locationFilter} 
                        onChange={(e)=>setLocationFilter(e.target.value)} 
                        className="w-full md:max-w-xs bg-white border border-gray-200 rounded-xl py-3 px-3 h-11 text-sm employee-company-search-location-select"
                    >
                        <option value="">All Locations</option>
                        {availableLocations.map(loc => (<option key={loc} value={loc}>{loc}</option>))}
                    </select>
                </div>
            </div>

            {/* Category chips (same UX as job board) - Desktop */}
            <div className="hidden md:flex flex-wrap gap-2 mb-6 employee-company-search-filters">
                {availableCategories.map((category) => (
                    <FilterChip
                        key={category}
                        label={category}
                        active={categoryFilter === category}
                        onClick={() => handleCategoryClick(category)}
                    />
                ))}
                {(categoryFilter || locationFilter || provinceFilter || searchQuery) && (
                    <button 
                        className="category-pill clear-all"
                        onClick={handleClearAll}
                    >
                        Clear All
                    </button>
                )}
            </div>

            {/* Category dropdown - Mobile */}
            <div className="md:hidden mb-6 employee-company-search-filters-mobile">
                <label className="block text-sm text-gray-700 mb-2">Browse by category</label>
                <select
                    value={categoryFilter}
                    onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                            handleClearAll();
                        } else {
                            handleCategoryClick(val);
                        }
                    }}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm"
                >
                    <option value="">All Categories</option>
                    {availableCategories.map((category) => (
                        <option key={category} value={category}>{category}</option>
                    ))}
                </select>
                {categoryFilter && (
                    <div className="employee-company-search-selected-category">
                        <span className={`category-pill ${getCategoryClass(categoryFilter)} active`}>
                            {categoryFilter}
                        </span>
                        <button 
                            className="category-pill clear-all"
                            onClick={handleClearAll}
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {/* Select filters like job board */}
            {/* Additional selects removed to keep layout aligned with new search bar */}

            <div className="text-xs text-gray-500 mb-4">Showing {companies.length} companies</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 employee-company-search-grid">
                {loading && <div className="col-span-full">Loading companies...</div>}
                {error && <div className="col-span-full text-red-600 text-sm">{error}</div>}
                {!loading && companies.map((c) => (
                    <CompanyCard key={c.name} company={c} />
                ))}
            </div>
        </div>
        </>
    );
};

export default EmployeeCompanySearch;


